/**
 * Video-first product gallery.
 *
 * Product detail pages only, every product, no per-product setup: if a product
 * has at least one Shopify-hosted video, the first video in the product's media
 * order becomes the media the gallery opens on, and it plays automatically -
 * muted, looping and inline. A product with no video keeps the hero it has
 * today and is left alone.
 *
 * The product's media order in Shopify admin is never touched. Only the
 * initially selected slide changes.
 *
 * Built on components the theme already ships:
 *   slider.js         install lifecycle, slideByElement(), the sliderChanged
 *                     event, and webvista.pauseAllMedia(), which the slider
 *                     already runs on every slide change
 *   media-gallery.js  changeMedia(), setActiveThumbnail()
 *   global.js         DeferredMedia.loadContent()
 *
 * Only the slide that is currently active is ever loaded or played, so every
 * other video keeps its deferred, preload="none" behaviour.
 */
(function () {
  'use strict';

  var MODALS = 'product-gallery-modal,quick-add-modal,modal-dialog,dialog';

  function productGalleries(root) {
    var nodes = (root || document).querySelectorAll('.section-main-product media-gallery');
    return Array.prototype.filter.call(nodes, function (gallery) {
      return !gallery.closest(MODALS);
    });
  }

  function firstVideoSlide(gallery) {
    return gallery.querySelector(
      '.product-media-list > .product-media-item[data-media-type="video"]'
    );
  }

  /**
   * Give the deferred <template> its playback attributes before the theme
   * clones it, so the video is muted, looping and inline from the moment it
   * exists. Looping also matters for correctness: without it the theme removes
   * the video element on "ended".
   */
  function prepareTemplate(slide) {
    var template = slide.querySelector('deferred-media template');
    if (!template || template.dataset.videoFirstPrepared) return;

    var video = template.content.querySelector('video');
    if (!video) return;

    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    template.dataset.videoFirstPrepared = 'true';
  }

  /**
   * The theme calls video.play() in two places without handling the promise
   * (DeferredMedia.loadContent and webvista.playAllMedia). Those calls only
   * began reaching these videos once the gallery started opening on one, so
   * give the elements this gallery owns a play() that absorbs the rejection a
   * browser raises when playback is blocked or interrupted. Nothing outside
   * this gallery is affected.
   */
  function hardenPlay(video) {
    if (!video || video.dataset.videoFirstHardened) return;

    var nativePlay = video.play.bind(video);
    video.play = function () {
      var started = nativePlay();
      if (started && typeof started.catch === 'function') {
        started.catch(function () {});
      }
      return started;
    };
    video.dataset.videoFirstHardened = 'true';
  }

  /**
   * Muted playback is the only kind browsers allow without a gesture, so mute
   * before asking. A rejected play() is an expected outcome rather than a
   * fault, and is swallowed so it never reaches the console.
   */
  function playMuted(video) {
    if (!video || video.tagName !== 'VIDEO') return;
    hardenPlay(video);

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');

    var started;
    try {
      started = video.play();
    } catch (error) {
      return;
    }
    if (started && typeof started.catch === 'function') {
      started.catch(function () {});
    }
  }

  /**
   * The slider already pauses every video in the gallery on each slide change
   * and replays the ones the new slide already holds. This supplies the half it
   * cannot do: loading a video the shopper has never opened.
   */
  function activateSlide(slide) {
    if (!slide || slide.dataset.mediaType !== 'video') return;

    var deferred = slide.querySelector('deferred-media');
    if (!deferred) return;

    var video = deferred.querySelector('video');
    if (video) {
      playMuted(video);
      return;
    }

    prepareTemplate(slide);
    if (typeof deferred.loadContent !== 'function') return;

    deferred.loadContent(false); // false: load without taking focus
    playMuted(deferred.querySelector('video'));
  }

  function openOn(gallery, viewer, slide) {
    var mediaId = slide.dataset.mediaId;

    var thumbnail = gallery.querySelector('[data-target="' + mediaId + '"]');
    if (thumbnail && typeof gallery.setActiveThumbnail === 'function') {
      gallery.setActiveThumbnail(thumbnail);
    }

    // The slider's second argument is its own "silent" flag: move with no
    // transition, so the gallery opens on the video rather than being seen to
    // slide to it.
    var moved = false;
    if (viewer && typeof viewer.slideByElement === 'function') {
      moved = viewer.slideByElement(slide, true) !== false;
    }
    if (!moved && typeof gallery.changeMedia === 'function') {
      gallery.changeMedia(mediaId); // stack layout, or no slider to drive
    }

    activateSlide(slide);
  }

  function setUp(gallery) {
    if (gallery.dataset.videoFirstBound) return;

    var slide = firstVideoSlide(gallery);
    if (!slide) return; // no video on this product: leave it exactly as it is

    gallery.dataset.videoFirstBound = 'true';
    prepareTemplate(slide);

    var viewer = gallery.querySelector('[id^="Gallery-Viewer"]');

    // Dispatched by DeferredMedia the moment the video is inserted and just
    // before the theme calls play() on it - the only point at which that
    // unhandled promise can still be caught.
    gallery.addEventListener('deferred-loaded', function (event) {
      var video = event.target.querySelector('video');
      if (video) hardenPlay(video);
    });

    gallery.addEventListener('sliderChanged', function (event) {
      if (viewer && event.target !== viewer) return; // not the thumbnail strip
      var current = event.detail && event.detail.currentElement;
      if (current) activateSlide(current);
    });

    if (!viewer || gallery.hasAttribute('data-media-stack') || viewer.initSliderStatus) {
      openOn(gallery, viewer, slide);
      return;
    }

    // slider.js installs itself from an IntersectionObserver, so hook the
    // lifecycle method it calls instead of guessing at a delay.
    var opened = false;
    var themeHook = viewer.hookAfterInstall;
    viewer.hookAfterInstall = function () {
      if (typeof themeHook === 'function') themeHook.apply(this, arguments);
      if (opened) return; // a resize reinstall must not pull the shopper back
      opened = true;
      openOn(gallery, viewer, slide);
    };
  }

  function init(root) {
    productGalleries(root).forEach(setUp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // The theme editor re-renders the section; bind the fresh markup.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
